import { Note } from 'tonal';

// src/tunings.ts
function createMicrotonalNote(baseNote, centsOffset, stringNumber, displayName) {
  const baseFreq = Note.freq(baseNote) ?? 0;
  const frequency = baseFreq * Math.pow(2, centsOffset / 1200);
  const pitchClass = Note.pitchClass(baseNote) || baseNote.replace(/\d/g, "");
  let name;
  if (displayName) {
    name = displayName;
  } else if (centsOffset === 0) {
    name = pitchClass;
  } else {
    const sign = centsOffset > 0 ? "+" : "\u2212";
    name = `${pitchClass}${sign}${Math.abs(centsOffset)}\xA2`;
  }
  return {
    note: baseNote,
    name,
    string: stringNumber,
    frequency,
    centsOffset
  };
}
function createMicrotonalTuning(id, name, notes, description) {
  const totalStrings = notes.length;
  return {
    id,
    name,
    notes: notes.map(
      (n, idx) => createMicrotonalNote(n.base, n.cents, totalStrings - idx, n.label)
    ),
    description
  };
}
var MICROTONAL_GUITAR_TUNINGS = [
  // —————————————————————————
  // JUST INTONATION TUNINGS
  // —————————————————————————
  createMicrotonalTuning(
    "guitar-just-open-d",
    "Just Intonation Open D",
    [
      { base: "D2", cents: 0, label: "D" },
      { base: "A2", cents: 2, label: "A\u2191" },
      { base: "D3", cents: 0, label: "D" },
      { base: "F#3", cents: -14, label: "F\u266F\u2193" },
      { base: "A3", cents: 2, label: "A\u2191" },
      { base: "D4", cents: 0, label: "D" }
    ],
    "Open D with pure intervals \u2014 sweet, beatless major chord"
  ),
  createMicrotonalTuning(
    "guitar-just-open-g",
    "Just Intonation Open G",
    [
      { base: "D2", cents: 2, label: "D\u2191" },
      { base: "G2", cents: 0, label: "G" },
      { base: "D3", cents: 2, label: "D\u2191" },
      { base: "G3", cents: 0, label: "G" },
      { base: "B3", cents: -14, label: "B\u2193" },
      { base: "D4", cents: 2, label: "D\u2191" }
    ],
    "Open G with pure intervals \u2014 try with a slide"
  ),
  createMicrotonalTuning(
    "guitar-just-open-e",
    "Just Intonation Open E",
    [
      { base: "E2", cents: 0, label: "E" },
      { base: "B2", cents: 2, label: "B\u2191" },
      { base: "E3", cents: 0, label: "E" },
      { base: "G#3", cents: -14, label: "G\u266F\u2193" },
      { base: "B3", cents: 2, label: "B\u2191" },
      { base: "E4", cents: 0, label: "E" }
    ],
    "Open E with pure intervals \u2014 ideal for slide in just intonation"
  ),
  createMicrotonalTuning(
    "guitar-just-open-am",
    "Just Intonation Open Am",
    [
      { base: "E2", cents: 2, label: "E\u2191" },
      { base: "A2", cents: 0, label: "A" },
      { base: "E3", cents: 2, label: "E\u2191" },
      { base: "A3", cents: 0, label: "A" },
      { base: "C4", cents: 16, label: "C\u2191" },
      { base: "E4", cents: 2, label: "E\u2191" }
    ],
    "Open Am with pure minor third (6:5) and pure fifth (3:2)"
  ),
  // —————————————————————————
  // QUARTER-TONE TUNINGS (24-TET)
  // —————————————————————————
  createMicrotonalTuning(
    "guitar-quarter-explorer",
    "Quarter-Tone Explorer",
    [
      { base: "E2", cents: 0, label: "E" },
      { base: "A2", cents: 0, label: "A" },
      { base: "D3", cents: 0, label: "D" },
      { base: "G3", cents: 50, label: "G\u2191\xBC" },
      { base: "B3", cents: 0, label: "B" },
      { base: "E4", cents: 0, label: "E" }
    ],
    "Standard with G string raised a quarter-tone \u2014 explore 24-TET intervals"
  ),
  createMicrotonalTuning(
    "guitar-maqam-rast",
    "Maqam Rast Explorer",
    [
      { base: "D2", cents: 0, label: "D" },
      { base: "A2", cents: 0, label: "A" },
      { base: "D3", cents: 0, label: "D" },
      { base: "G3", cents: 0, label: "G" },
      { base: "B3", cents: -50, label: "B\u2193\xBC" },
      { base: "E4", cents: 0, label: "E" }
    ],
    "Open strings approximating Maqam Rast intervals \u2014 D Rast scale"
  ),
  createMicrotonalTuning(
    "guitar-quarter-full",
    "Quarter-Tone Alternating",
    [
      { base: "E2", cents: 0, label: "E" },
      { base: "A2", cents: 50, label: "A\u2191\xBC" },
      { base: "D3", cents: 0, label: "D" },
      { base: "G3", cents: 50, label: "G\u2191\xBC" },
      { base: "B3", cents: 0, label: "B" },
      { base: "E4", cents: 50, label: "E\u2191\xBC" }
    ],
    "Alternating strings offset by quarter-tones \u2014 full 24-TET access across strings"
  ),
  // —————————————————————————
  // HARMONIC SERIES / SPECTRAL TUNINGS
  // —————————————————————————
  createMicrotonalTuning(
    "guitar-harmonic-series-d",
    "Harmonic Series (D)",
    [
      { base: "D2", cents: 0, label: "D (H2)" },
      { base: "A2", cents: 2, label: "A\u2191 (H3)" },
      { base: "D3", cents: 0, label: "D (H4)" },
      { base: "F#3", cents: -14, label: "F\u266F\u2193 (H5)" },
      { base: "A3", cents: 2, label: "A\u2191 (H6)" },
      { base: "C4", cents: -31, label: "C\u266E\u2193 (H7)" }
    ],
    "Strings follow the natural harmonic series of D \u2014 deeply resonant, alien intervals"
  ),
  createMicrotonalTuning(
    "guitar-harmonic-series-e",
    "Harmonic Series (E)",
    [
      { base: "E2", cents: 0, label: "E (H2)" },
      { base: "B2", cents: 2, label: "B\u2191 (H3)" },
      { base: "E3", cents: 0, label: "E (H4)" },
      { base: "G#3", cents: -14, label: "G\u266F\u2193 (H5)" },
      { base: "B3", cents: 2, label: "B\u2191 (H6)" },
      { base: "D4", cents: -31, label: "D\u2193 (H7)" }
    ],
    "Harmonic series of E \u2014 the 7th harmonic D is strikingly blue"
  ),
  // —————————————————————————
  // 7-LIMIT / SEPTIMAL TUNINGS
  // —————————————————————————
  createMicrotonalTuning(
    "guitar-septimal-blues",
    "Septimal Blues (Open D7)",
    [
      { base: "D2", cents: 0, label: "D" },
      { base: "A2", cents: 2, label: "A\u2191" },
      { base: "D3", cents: 0, label: "D" },
      { base: "F#3", cents: -14, label: "F\u266F\u2193" },
      { base: "A3", cents: 2, label: "A\u2191" },
      { base: "C4", cents: -31, label: "C\u266E\u2193" }
    ],
    'Open D with a pure harmonic 7th \u2014 the "blue note" from the overtone series'
  ),
  // —————————————————————————
  // GAMELAN-INSPIRED / STRETCHED TUNINGS
  // —————————————————————————
  createMicrotonalTuning(
    "guitar-slendro",
    "Slendro-Inspired",
    [
      { base: "E2", cents: 0, label: "1" },
      { base: "G2", cents: 40, label: "2" },
      { base: "A2", cents: -20, label: "3" },
      { base: "C3", cents: 20, label: "4" },
      { base: "D3", cents: -40, label: "5" },
      { base: "E3", cents: 0, label: "1'" }
    ],
    "Approximation of Javanese Slendro scale (5-TET) \u2014 ethereal, gamelan-like"
  ),
  createMicrotonalTuning(
    "guitar-pelog",
    "Pelog-Inspired",
    [
      { base: "E2", cents: 0, label: "1" },
      { base: "F2", cents: 25, label: "2" },
      { base: "G#2", cents: -15, label: "3" },
      { base: "A2", cents: 40, label: "4" },
      { base: "C3", cents: -20, label: "5" },
      { base: "E3", cents: 0, label: "1'" }
    ],
    "Approximation of Javanese Pelog scale \u2014 haunting, asymmetric intervals"
  ),
  // —————————————————————————
  // EXPERIMENTAL / COMPOSITIONAL
  // —————————————————————————
  createMicrotonalTuning(
    "guitar-19tet-fourths",
    "19-TET Fourths",
    [
      { base: "E2", cents: 0, label: "E" },
      { base: "A2", cents: -5, label: "A\u2193" },
      { base: "D3", cents: -11, label: "D\u2193" },
      { base: "G3", cents: -16, label: "G\u2193" },
      { base: "B3", cents: -5, label: "B\u2193" },
      { base: "E4", cents: -11, label: "E\u2193" }
    ],
    "19-TET approximation using standard string layout \u2014 sweeter thirds"
  ),
  createMicrotonalTuning(
    "guitar-shruti-d",
    "Shruti-Inspired (Sa=D)",
    [
      { base: "D2", cents: 0, label: "Sa" },
      { base: "A2", cents: 2, label: "Pa" },
      { base: "D3", cents: 0, label: "Sa" },
      { base: "F3", cents: 16, label: "ga" },
      { base: "A3", cents: 2, label: "Pa" },
      { base: "D4", cents: 0, label: "Sa" }
    ],
    "Open strings for Raga exploration \u2014 Sa-Pa-ga with pure intervals"
  ),
  createMicrotonalTuning(
    "guitar-pure-fifths-chain",
    "Pure Fifths Chain",
    [
      { base: "D2", cents: 0, label: "D" },
      { base: "A2", cents: 2, label: "A\u2191" },
      { base: "E3", cents: 4, label: "E\u2191\u2191" },
      { base: "B3", cents: 6, label: "B\u2191\u2191\u2191" },
      { base: "F#4", cents: 8, label: "F\u266F\u2191" },
      { base: "C#5", cents: 10, label: "C\u266F\u2191" }
    ],
    "Pythagorean chain of pure 3:2 fifths \u2014 drift from 12-TET accumulates"
  )
];
var MICROTONAL_CATEGORY = {
  id: "microtonal-guitar",
  name: "Microtonal Guitar",
  icon: "\u{1F3B8}",
  tunings: MICROTONAL_GUITAR_TUNINGS
};

// src/tunings.ts
function createNote(noteStr, stringNumber) {
  const freq = Note.freq(noteStr);
  const noteName = Note.pitchClass(noteStr) || noteStr.replace(/\d/g, "");
  return {
    note: noteStr,
    name: noteName,
    string: stringNumber,
    frequency: freq || 0
  };
}
function createTuning(id, name, notes, description) {
  const totalStrings = notes.length;
  return {
    id,
    name,
    notes: notes.map(
      (note, idx) => createNote(note, totalStrings - idx)
    ),
    description
  };
}
var GUITAR_TUNINGS = [
  // Standard and Common
  createTuning("guitar-standard", "Standard", ["E2", "A2", "D3", "G3", "B3", "E4"], "EADGBE - Most common guitar tuning"),
  createTuning("guitar-drop-d", "Drop D", ["D2", "A2", "D3", "G3", "B3", "E4"], "DADGBE - Popular for rock and metal"),
  createTuning("guitar-drop-c", "Drop C", ["C2", "G2", "C3", "F3", "A3", "D4"], "CGCFAD - Heavy metal tuning"),
  createTuning("guitar-drop-b", "Drop B", ["B1", "F#2", "B2", "E3", "G#3", "C#4"], "BF#BEG#C# - Extra heavy tuning"),
  createTuning("guitar-drop-a", "Drop A", ["A1", "E2", "A2", "D3", "F#3", "B3"], "AEADF#B - Very low tuning"),
  // Half/Full Step Down
  createTuning("guitar-half-step-down", "Half Step Down (Eb Standard)", ["Eb2", "Ab2", "Db3", "Gb3", "Bb3", "Eb4"], "Eb Ab Db Gb Bb Eb - Hendrix, SRV, easier on strings"),
  createTuning("guitar-full-step-down", "Full Step Down (D Standard)", ["D2", "G2", "C3", "F3", "A3", "D4"], "DGCFAD - One whole step down"),
  // Open Tunings
  createTuning("guitar-open-d", "Open D", ["D2", "A2", "D3", "F#3", "A3", "D4"], "DADF#AD - Blues and slide guitar"),
  createTuning("guitar-open-e", "Open E", ["E2", "B2", "E3", "G#3", "B3", "E4"], "EBEG#BE - Slide guitar"),
  createTuning("guitar-open-g", "Open G", ["D2", "G2", "D3", "G3", "B3", "D4"], "DGDGBD - Rolling Stones, Keith Richards"),
  createTuning("guitar-open-a", "Open A", ["E2", "A2", "E3", "A3", "C#4", "E4"], "EAEAC#E - Slide guitar"),
  createTuning("guitar-open-c", "Open C", ["C2", "G2", "C3", "G3", "C4", "E4"], "CGCGCE - John Fahey, Led Zeppelin"),
  createTuning("guitar-open-c6", "Open C6", ["C2", "G2", "C3", "G3", "A3", "E4"], "CGCGAE - C6 tuning"),
  // DADGAD and Variants
  createTuning("guitar-dadgad", "DADGAD", ["D2", "A2", "D3", "G3", "A3", "D4"], "DADGAD - Celtic, folk music"),
  createTuning("guitar-double-drop-d", "Double Drop D (DADGBD)", ["D2", "A2", "D3", "G3", "B3", "D4"], "DADGBD - Both E strings dropped to D"),
  // Nick Drake Tunings
  createTuning("guitar-nick-drake-1", "Nick Drake Tuning 1", ["C2", "G2", "C3", "F3", "C4", "E4"], "CGCFCE - River Man"),
  createTuning("guitar-nick-drake-2", "Nick Drake Tuning 2", ["D2", "A2", "D3", "G3", "A3", "D4"], "DADGAD - Pink Moon"),
  createTuning("guitar-nick-drake-3", "Nick Drake Tuning 3", ["C2", "G2", "C3", "F3", "C4", "D4"], "CGCFCD - Place to Be"),
  createTuning("guitar-bebebe", "BEBEBE", ["B1", "E2", "B2", "E3", "B3", "E4"], "BEBEBE - Michael Hedges"),
  // Nashville Tuning (high strung)
  createTuning("guitar-nashville", "Nashville", ["E3", "A3", "D4", "G4", "B3", "E4"], "E A D G B E (high) - Chimey, doubled sound"),
  // Baritone
  createTuning("guitar-baritone-standard", "Baritone Standard", ["B1", "E2", "A2", "D3", "F#3", "B3"], "BEADF#B - Baritone guitar"),
  createTuning("guitar-baritone-a", "Baritone A Standard", ["A1", "D2", "G2", "C3", "E3", "A3"], "ADGCEA - Lower baritone")
];
var GUITAR_7_STRING_TUNINGS = [
  createTuning("guitar7-standard", "7-String Standard", ["B1", "E2", "A2", "D3", "G3", "B3", "E4"], "BEADGBE - Standard 7-string"),
  createTuning("guitar7-drop-a", "7-String Drop A", ["A1", "E2", "A2", "D3", "G3", "B3", "E4"], "AEADGBE - Drop tuning"),
  createTuning("guitar7-drop-g", "7-String Drop G", ["G1", "D2", "G2", "C3", "F3", "A3", "D4"], "GDGCFAD - Very heavy")
];
var GUITAR_8_STRING_TUNINGS = [
  createTuning("guitar8-standard", "8-String Standard", ["F#1", "B1", "E2", "A2", "D3", "G3", "B3", "E4"], "F#BEADGBE - Standard 8-string"),
  createTuning("guitar8-drop-e", "8-String Drop E", ["E1", "B1", "E2", "A2", "D3", "G3", "B3", "E4"], "EBEADGBE - Drop tuning")
];
var BASS_4_TUNINGS = [
  createTuning("bass4-standard", "Standard", ["E1", "A1", "D2", "G2"], "EADG - Standard 4-string bass"),
  createTuning("bass4-drop-d", "Drop D", ["D1", "A1", "D2", "G2"], "DADG - Drop D bass"),
  createTuning("bass4-drop-c", "Drop C", ["C1", "G1", "C2", "F2"], "CGCF - Heavy bass"),
  createTuning("bass4-half-step-down", "Half Step Down", ["Eb1", "Ab1", "Db2", "Gb2"], "Eb Ab Db Gb"),
  createTuning("bass4-full-step-down", "Full Step Down", ["D1", "G1", "C2", "F2"], "DGCF - D Standard bass"),
  createTuning("bass4-drop-b", "Drop B", ["B0", "F#1", "B1", "E2"], "BF#BE - Very low"),
  createTuning("bass4-hipshot-d", "Hipshot D", ["D1", "A1", "D2", "G2"], "DADG - Same as Drop D"),
  createTuning("bass4-piccolo", "Piccolo", ["E2", "A2", "D3", "G3"], "EADG (octave up) - Piccolo bass")
];
var BASS_5_TUNINGS = [
  createTuning("bass5-standard", "5-String Standard", ["B0", "E1", "A1", "D2", "G2"], "BEADG - Standard 5-string"),
  createTuning("bass5-standard-high", "5-String High C", ["E1", "A1", "D2", "G2", "C3"], "EADGC - High C string"),
  createTuning("bass5-drop-a", "5-String Drop A", ["A0", "E1", "A1", "D2", "G2"], "AEADG - Drop A"),
  createTuning("bass5-half-step-down", "5-String Half Step Down", ["Bb0", "Eb1", "Ab1", "Db2", "Gb2"], "Bb Eb Ab Db Gb")
];
var BASS_6_TUNINGS = [
  createTuning("bass6-standard", "6-String Standard", ["B0", "E1", "A1", "D2", "G2", "C3"], "BEADGC - Standard 6-string bass"),
  createTuning("bass6-drop-a", "6-String Drop A", ["A0", "E1", "A1", "D2", "G2", "C3"], "AEADGC - Drop A 6-string")
];
var UKULELE_TUNINGS = [
  // Soprano, Concert, Tenor (standard size)
  createTuning("uke-standard", "Standard (gCEA)", ["G4", "C4", "E4", "A4"], "GCEA - Re-entrant tuning"),
  createTuning("uke-low-g", "Low G", ["G3", "C4", "E4", "A4"], "GCEA - Low G, linear tuning"),
  createTuning("uke-slack-key", "Slack-Key", ["G4", "C4", "E4", "G4"], "GCEG - Hawaiian"),
  createTuning("uke-d-tuning", "D Tuning", ["A4", "D4", "F#4", "B4"], "ADF#B - Higher pitch"),
  createTuning("uke-english", "English/Canadian", ["A4", "D4", "F#4", "B4"], "ADF#B - Same as D Tuning"),
  createTuning("uke-open-g", "Open G", ["G4", "D4", "G4", "B4"], "GDGB - Open G chord"),
  createTuning("uke-open-c", "Open C", ["G4", "C4", "E4", "G4"], "GCEG - Open C chord"),
  // Baritone
  createTuning("uke-baritone", "Baritone", ["D3", "G3", "B3", "E4"], "DGBE - Same as guitar strings 4-1"),
  createTuning("uke-baritone-low-d", "Baritone Low D", ["D2", "G3", "B3", "E4"], "DGBE - Very low D"),
  // 6-string Ukulele
  createTuning("uke-guitalele", "Guitalele", ["A2", "D3", "G3", "C4", "E4", "A4"], "ADGCEA - 6-string ukulele")
];
var VIOLIN_TUNINGS = [
  createTuning("violin-standard", "Standard", ["G3", "D4", "A4", "E5"], "GDAE - Standard violin"),
  createTuning("violin-cajun", "Cajun", ["F3", "C4", "G4", "D5"], "FCGD - One step down"),
  createTuning("violin-cross", "Cross Tuning (AEAE)", ["A3", "E4", "A4", "E5"], "AEAE - Old-time fiddle"),
  createTuning("violin-calico", "Calico (GDGD)", ["G3", "D4", "G4", "D5"], "GDGD - Drone tuning"),
  createTuning("violin-open-g", "Open G (GDGB)", ["G3", "D4", "G4", "B4"], "GDGB - Open G"),
  createTuning("violin-high-bass", "High Bass (ADAE)", ["A3", "D4", "A4", "E5"], "ADAE - Appalachian")
];
var VIOLA_TUNINGS = [
  createTuning("viola-standard", "Standard", ["C3", "G3", "D4", "A4"], "CGDA - Standard viola")
];
var CELLO_TUNINGS = [
  createTuning("cello-standard", "Standard", ["C2", "G2", "D3", "A3"], "CGDA - Standard cello"),
  createTuning("cello-fifth-down", "Fifth String Down", ["B1", "C2", "G2", "D3", "A3"], "BCGDA - 5-string cello")
];
var DOUBLE_BASS_TUNINGS = [
  createTuning("doublebass-standard", "Standard", ["E1", "A1", "D2", "G2"], "EADG - Standard double bass"),
  createTuning("doublebass-5string", "5-String Low B", ["B0", "E1", "A1", "D2", "G2"], "BEADG - Extended range"),
  createTuning("doublebass-5string-c", "5-String High C", ["E1", "A1", "D2", "G2", "C3"], "EADGC - High C"),
  createTuning("doublebass-solo", "Solo Tuning", ["F#1", "B1", "E2", "A2"], "F#BEA - Raised a step"),
  createTuning("doublebass-orchestra", "Orchestra Drop D", ["D1", "A1", "D2", "G2"], "DADG - Drop D")
];
var MANDOLIN_TUNINGS = [
  createTuning("mandolin-standard", "Standard", ["G3", "D4", "A4", "E5"], "GDAE - Standard mandolin (doubled)"),
  createTuning("mandolin-octave", "Octave Mandolin", ["G2", "D3", "A3", "E4"], "GDAE - One octave lower"),
  createTuning("mandolin-irish", "Irish Bouzouki", ["G2", "D3", "A3", "D4"], "GDAD - Irish tuning"),
  createTuning("mandolin-open-g", "Open G", ["G3", "D4", "G4", "B4"], "GDGB - Open G chord"),
  createTuning("mandolin-saw-mill", "Saw Mill", ["G3", "D4", "G4", "D5"], "GDGD - Old-time"),
  createTuning("mandolin-calico", "Calico", ["G3", "D4", "A4", "D5"], "GDAD - Cross tuning")
];
var MANDOLA_TUNINGS = [
  createTuning("mandola-standard", "Standard", ["C3", "G3", "D4", "A4"], "CGDA - Standard mandola")
];
var MANDOCELLO_TUNINGS = [
  createTuning("mandocello-standard", "Standard", ["C2", "G2", "D3", "A3"], "CGDA - Standard mandocello")
];
var BANJO_5_TUNINGS = [
  createTuning("banjo5-open-g", "Open G", ["G4", "D3", "G3", "B3", "D4"], "gDGBD - Standard 5-string"),
  createTuning("banjo5-open-d", "Open D", ["A4", "D3", "F#3", "A3", "D4"], "aDF#AD - Bluegrass"),
  createTuning("banjo5-double-c", "Double C", ["G4", "C3", "G3", "C4", "D4"], "gCGCD - Old-time"),
  createTuning("banjo5-saw-mill", "Saw Mill", ["G4", "D3", "G3", "C4", "D4"], "gDGCD - Old-time"),
  createTuning("banjo5-standard-c", "Standard C", ["G4", "C3", "G3", "B3", "D4"], "gCGBD - C tuning"),
  createTuning("banjo5-old-time-d", "Old-Time D", ["F#4", "D3", "F#3", "A3", "D4"], "f#DF#AD"),
  createTuning("banjo5-open-a", "Open A", ["A4", "E3", "A3", "C#4", "E4"], "aEAC#E"),
  createTuning("banjo5-double-d", "Double D", ["A4", "D3", "A3", "D4", "E4"], "aDADE - Old-time")
];
var BANJO_4_TUNINGS = [
  createTuning("banjo4-standard", "Standard (Chicago)", ["C3", "G3", "B3", "D4"], "CGBD - Plectrum banjo"),
  createTuning("banjo4-irish", "Irish Tenor", ["G2", "D3", "A3", "E4"], "GDAE - Like mandolin"),
  createTuning("banjo4-jazz", "Jazz Tenor", ["C3", "G3", "D4", "A4"], "CGDA - Like viola")
];
var BANJO_6_TUNINGS = [
  createTuning("banjo6-standard", "Standard (Banjitar)", ["E2", "A2", "D3", "G3", "B3", "E4"], "EADGBE - Guitar tuning on banjo")
];
var CHARANGO_TUNINGS = [
  createTuning("charango-standard", "Standard", ["G4", "C4", "E4", "A4", "E5"], "GCEAE - Andean tuning")
];
var CAVAQUINHO_TUNINGS = [
  createTuning("cavaquinho-brazil", "Brazilian", ["D4", "G4", "B4", "D5"], "DGBD - Samba"),
  createTuning("cavaquinho-portugal", "Portuguese", ["D4", "A4", "B4", "E5"], "DABE - Fado")
];
var BOUZOUKI_TUNINGS = [
  createTuning("bouzouki-greek", "Greek", ["D3", "A3", "D4"], "DAD - Traditional 3-course"),
  createTuning("bouzouki-greek-4", "Greek 4-Course", ["C3", "F3", "A3", "D4"], "CFAD - Modern Greek"),
  createTuning("bouzouki-irish", "Irish", ["G2", "D3", "A3", "D4"], "GDAD - Irish bouzouki"),
  createTuning("bouzouki-irish-octave", "Irish Octave", ["G2", "D3", "A3", "E4"], "GDAE - Octave mandolin")
];
var LAP_STEEL_TUNINGS = [
  createTuning("lapsteel-c6", "C6", ["C3", "E3", "G3", "A3", "C4", "E4"], "CEGACE - C6 tuning"),
  createTuning("lapsteel-open-d", "Open D", ["D2", "A2", "D3", "F#3", "A3", "D4"], "DADF#AD"),
  createTuning("lapsteel-open-e", "Open E", ["E2", "B2", "E3", "G#3", "B3", "E4"], "EBEG#BE"),
  createTuning("lapsteel-open-g", "Open G", ["G2", "B2", "D3", "G3", "B3", "D4"], "GBDGBD"),
  createTuning("lapsteel-a6", "A6", ["E3", "C#3", "A2", "F#3", "E3", "C#4"], "EC#AF#EC# - A6 tuning")
];
var PEDAL_STEEL_TUNINGS = [
  createTuning("pedalsteel-e9", "E9", ["B2", "D3", "E3", "F#3", "G#3", "B3", "E4", "G#4", "D#4", "F#4"], "E9 Nashville"),
  createTuning("pedalsteel-c6", "C6", ["C2", "F2", "A2", "C3", "E3", "G3", "A3", "C4", "E4", "G4"], "C6 Universal")
];
var CIGAR_BOX_TUNINGS = [
  createTuning("cigarbox-open-g", "Open G (3-string)", ["G3", "B3", "D4"], "GBD - Open G chord"),
  createTuning("cigarbox-open-d", "Open D (3-string)", ["D3", "F#3", "A3"], "DF#A - Open D chord"),
  createTuning("cigarbox-gdg", "GDG (3-string)", ["G3", "D4", "G4"], "GDG - Fifths tuning"),
  createTuning("cigarbox-4-open-g", "Open G (4-string)", ["D3", "G3", "B3", "D4"], "DGBD")
];
var DULCIMER_TUNINGS = [
  createTuning("dulcimer-dad", "DAD (Ionian)", ["D3", "A3", "D4"], "DAD - Standard Appalachian"),
  createTuning("dulcimer-daa", "DAA (Mixolydian)", ["D3", "A3", "A3"], "DAA - Older style"),
  createTuning("dulcimer-dag", "DAG (Dorian)", ["D3", "A3", "G3"], "DAG - Dorian mode"),
  createTuning("dulcimer-dgd", "DGD (Bagpipe)", ["D3", "G3", "D4"], "DGD - Drone tuning"),
  createTuning("dulcimer-dac", "DAC (Aeolian)", ["D3", "A3", "C4"], "DAC - Minor key")
];
var SITAR_TUNINGS = [
  createTuning("sitar-kharaj", "Kharaj Pancham", ["C2", "G2", "C3", "C3", "G3", "C4", "F4"], "Standard North Indian"),
  createTuning("sitar-gandhar", "Gandhar Pancham", ["C2", "G2", "C3", "E3", "G3", "C4", "F4"], "Gandhar variant")
];
var OUD_TUNINGS = [
  createTuning("oud-arabic", "Arabic Standard", ["C2", "F2", "A2", "D3", "G3", "C4"], "CFADGC - 6 courses"),
  createTuning("oud-turkish", "Turkish", ["D2", "A2", "B2", "E3", "A3", "D4"], "DABEAE - Turkish style"),
  createTuning("oud-iraqi", "Iraqi", ["C2", "F2", "A2", "D3", "G3", "C4"], "CFADGC - Same as Arabic")
];
var PIPA_TUNINGS = [
  createTuning("pipa-standard", "Standard", ["A2", "D3", "E3", "A3"], "ADEA - Chinese pipa")
];
var SHAMISEN_TUNINGS = [
  createTuning("shamisen-honchoshi", "Honchoshi", ["C3", "G3", "C4"], "CGC - Basic tuning"),
  createTuning("shamisen-niagari", "Niagari", ["C3", "G3", "D4"], "CGD - Raised third"),
  createTuning("shamisen-sansagari", "Sansagari", ["C3", "F3", "C4"], "CFC - Lowered second")
];
var AUTOHARP_TUNINGS = [
  createTuning("autoharp-diatonic", "Diatonic", ["F2", "G2", "A2", "Bb2", "C3", "D3", "E3", "F3", "G3", "A3", "Bb3", "C4", "D4", "E4", "F4"], "Diatonic scale")
];
var INSTRUMENT_CATEGORIES = [
  {
    id: "guitar",
    name: "Guitar (6-string)",
    icon: "\u{1F3B8}",
    tunings: GUITAR_TUNINGS
  },
  {
    id: "guitar-7",
    name: "Guitar (7-string)",
    icon: "\u{1F3B8}",
    tunings: GUITAR_7_STRING_TUNINGS
  },
  {
    id: "guitar-8",
    name: "Guitar (8-string)",
    icon: "\u{1F3B8}",
    tunings: GUITAR_8_STRING_TUNINGS
  },
  {
    id: "bass-4",
    name: "Bass (4-string)",
    icon: "\u{1F3B8}",
    tunings: BASS_4_TUNINGS
  },
  {
    id: "bass-5",
    name: "Bass (5-string)",
    icon: "\u{1F3B8}",
    tunings: BASS_5_TUNINGS
  },
  {
    id: "bass-6",
    name: "Bass (6-string)",
    icon: "\u{1F3B8}",
    tunings: BASS_6_TUNINGS
  },
  {
    id: "ukulele",
    name: "Ukulele",
    icon: "\u{1FA95}",
    tunings: UKULELE_TUNINGS
  },
  {
    id: "violin",
    name: "Violin/Fiddle",
    icon: "\u{1F3BB}",
    tunings: VIOLIN_TUNINGS
  },
  {
    id: "viola",
    name: "Viola",
    icon: "\u{1F3BB}",
    tunings: VIOLA_TUNINGS
  },
  {
    id: "cello",
    name: "Cello",
    icon: "\u{1F3BB}",
    tunings: CELLO_TUNINGS
  },
  {
    id: "double-bass",
    name: "Double Bass",
    icon: "\u{1F3BB}",
    tunings: DOUBLE_BASS_TUNINGS
  },
  {
    id: "mandolin",
    name: "Mandolin",
    icon: "\u{1FA95}",
    tunings: MANDOLIN_TUNINGS
  },
  {
    id: "mandola",
    name: "Mandola",
    icon: "\u{1FA95}",
    tunings: MANDOLA_TUNINGS
  },
  {
    id: "mandocello",
    name: "Mandocello",
    icon: "\u{1FA95}",
    tunings: MANDOCELLO_TUNINGS
  },
  {
    id: "banjo-5",
    name: "Banjo (5-string)",
    icon: "\u{1FA95}",
    tunings: BANJO_5_TUNINGS
  },
  {
    id: "banjo-4",
    name: "Banjo (4-string)",
    icon: "\u{1FA95}",
    tunings: BANJO_4_TUNINGS
  },
  {
    id: "banjo-6",
    name: "Banjo (6-string)",
    icon: "\u{1FA95}",
    tunings: BANJO_6_TUNINGS
  },
  {
    id: "charango",
    name: "Charango",
    icon: "\u{1F3B8}",
    tunings: CHARANGO_TUNINGS
  },
  {
    id: "cavaquinho",
    name: "Cavaquinho",
    icon: "\u{1F3B8}",
    tunings: CAVAQUINHO_TUNINGS
  },
  {
    id: "bouzouki",
    name: "Bouzouki",
    icon: "\u{1F3B8}",
    tunings: BOUZOUKI_TUNINGS
  },
  {
    id: "lap-steel",
    name: "Lap Steel",
    icon: "\u{1F3B8}",
    tunings: LAP_STEEL_TUNINGS
  },
  {
    id: "cigar-box",
    name: "Cigar Box Guitar",
    icon: "\u{1F3B8}",
    tunings: CIGAR_BOX_TUNINGS
  },
  {
    id: "dulcimer",
    name: "Appalachian Dulcimer",
    icon: "\u{1F3B8}",
    tunings: DULCIMER_TUNINGS
  },
  {
    id: "shamisen",
    name: "Shamisen",
    icon: "\u{1F3B8}",
    tunings: SHAMISEN_TUNINGS
  },
  {
    id: "oud",
    name: "Oud",
    icon: "\u{1F3B8}",
    tunings: OUD_TUNINGS
  },
  {
    id: "pipa",
    name: "Pipa",
    icon: "\u{1F3B8}",
    tunings: PIPA_TUNINGS
  },
  MICROTONAL_CATEGORY
];
function serializeTuning(tuning) {
  const sortedNotes = [...tuning.notes].sort((a, b) => b.string - a.string);
  return sortedNotes.map((n) => n.note).join("-");
}
function parseTuningString(tuningStr) {
  return tuningStr.split("-").filter((s) => s.length > 0);
}
function createCustomTuning(notes, name) {
  const validNotes = notes.filter((n) => Note.freq(n) !== null);
  if (validNotes.length !== notes.length || notes.length === 0) {
    return null;
  }
  return createTuning(
    "custom-" + Date.now(),
    name || "Custom Tuning",
    validNotes,
    `Custom ${notes.length}-string tuning`
  );
}
function findTuningById(id) {
  for (const category of INSTRUMENT_CATEGORIES) {
    const tuning = category.tunings.find((t) => t.id === id);
    if (tuning) return tuning;
  }
  return null;
}
function findTuningByNotes(notes) {
  const normalizedInput = notes.map((n) => Note.simplify(n) || n).join("-");
  for (const category of INSTRUMENT_CATEGORIES) {
    for (const tuning of category.tunings) {
      const tuningNotes = [...tuning.notes].sort((a, b) => b.string - a.string).map((n) => Note.simplify(n.note) || n.note).join("-");
      if (tuningNotes === normalizedInput) {
        return tuning;
      }
    }
  }
  return null;
}
function getDefaultTuning(categoryId) {
  const category = INSTRUMENT_CATEGORIES.find((c) => c.id === categoryId);
  return category?.tunings[0] || null;
}
function getAllTunings() {
  return INSTRUMENT_CATEGORIES.flatMap((c) => c.tunings);
}
function searchTunings(query) {
  const lowerQuery = query.toLowerCase();
  const results = [];
  for (const category of INSTRUMENT_CATEGORIES) {
    for (const tuning of category.tunings) {
      if (tuning.name.toLowerCase().includes(lowerQuery) || tuning.description?.toLowerCase().includes(lowerQuery) || tuning.notes.some((n) => n.note.toLowerCase().includes(lowerQuery))) {
        results.push({ tuning, category });
      }
    }
  }
  return results;
}

// src/navigation.ts
var SECTION_KEYWORDS = [
  { id: "standard", name: "Standard", test: (name) => name.includes("standard") },
  { id: "drop", name: "Drop", test: (name) => name.includes("drop") },
  { id: "open", name: "Open", test: (name) => name.includes("open") },
  { id: "down-tuned", name: "Down Tuned", test: (name) => name.includes("half step down") || name.includes("full step down") || name.includes("step down") },
  { id: "alternate", name: "Alternate", test: (name) => name.includes("dadgad") || name.includes("double drop") || name.includes("saw mill") || name.includes("cross") || name.includes("calico") },
  { id: "artist-style", name: "Artist / Style", test: (name) => name.includes("nick drake") || name.includes("nashville") || name.includes("cajun") || name.includes("irish") || name.includes("jazz") },
  { id: "extended-range", name: "Extended Range", test: (name) => name.includes("baritone") || name.includes("octave") || name.includes("5-string") || name.includes("6-string") || name.includes("7-string") || name.includes("8-string") }
];
function getSectionMeta(tuning) {
  const name = tuning.name.toLowerCase();
  for (const section of SECTION_KEYWORDS) {
    if (section.test(name)) {
      return { id: section.id, name: section.name };
    }
  }
  return { id: "other", name: "Other" };
}
function getInstrumentById(instrumentId) {
  return INSTRUMENT_CATEGORIES.find((instrument) => instrument.id === instrumentId) || null;
}
function getInstrumentForTuning(tuningId) {
  return INSTRUMENT_CATEGORIES.find((instrument) => instrument.tunings.some((tuning) => tuning.id === tuningId)) || null;
}
function getSectionsForInstrument(instrumentId) {
  const instrument = getInstrumentById(instrumentId);
  if (!instrument) return [];
  const sectionMap = /* @__PURE__ */ new Map();
  for (const tuning of instrument.tunings) {
    const section = getSectionMeta(tuning);
    const existing = sectionMap.get(section.id);
    if (existing) {
      existing.tunings.push(tuning);
    } else {
      sectionMap.set(section.id, {
        id: section.id,
        name: section.name,
        tunings: [tuning]
      });
    }
  }
  return [...sectionMap.values()].sort((a, b) => a.name.localeCompare(b.name));
}
function getSectionForTuning(instrumentId, tuningId) {
  const sections = getSectionsForInstrument(instrumentId);
  return sections.find((section) => section.tunings.some((tuning) => tuning.id === tuningId)) || null;
}
function getSectionById(instrumentId, sectionId) {
  const sections = getSectionsForInstrument(instrumentId);
  return sections.find((section) => section.id === sectionId) || null;
}

// src/featured.ts
var FEATURED_TUNING_IDS = [
  "guitar-standard",
  "guitar-drop-d",
  "guitar-half-step-down",
  "guitar-dadgad",
  "guitar-open-g",
  "guitar-open-d",
  "bass4-standard",
  "uke-standard",
  "violin-standard",
  "mandolin-standard",
  "banjo5-open-g",
  "cello-standard",
  "guitar7-standard",
  "guitar-just-open-d"
];
function getFeaturedTunings() {
  const featured = [];
  for (const id of FEATURED_TUNING_IDS) {
    const tuning = findTuningById(id);
    const instrument = getInstrumentForTuning(id);
    if (tuning && instrument) {
      featured.push({ tuning, instrument });
    }
  }
  return featured;
}

// src/reference-pitch.ts
var STANDARD_A4 = 440;
var MIN_REFERENCE_PITCH = 415;
var MAX_REFERENCE_PITCH = 466;
var REFERENCE_PITCH_PRESETS = [
  {
    hz: 440,
    label: "440 Hz",
    description: "Concert standard (ISO 16)"
  },
  {
    hz: 432,
    label: "432 Hz",
    description: "Verdi tuning \u2014 every frequency scaled by exactly 432/440"
  }
];
function clampReferencePitch(hz) {
  if (!Number.isFinite(hz)) return STANDARD_A4;
  return Math.min(MAX_REFERENCE_PITCH, Math.max(MIN_REFERENCE_PITCH, hz));
}
function midiToFrequency(midi, a4 = STANDARD_A4) {
  return a4 * Math.pow(2, (midi - 69) / 12);
}
var PITCH_CLASSES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
function frequencyToNote(frequency, a4 = STANDARD_A4) {
  if (!(frequency > 0) || !(a4 > 0)) return null;
  const midiFloat = 12 * Math.log2(frequency / a4) + 69;
  const midi = Math.round(midiFloat);
  if (midi < 0 || midi > 127) return null;
  const octave = Math.floor(midi / 12) - 1;
  const name = `${PITCH_CLASSES[midi % 12]}${octave}`;
  return {
    name,
    midi,
    cents: (midiFloat - midi) * 100
  };
}
function scaleNote(note, ratio) {
  return { ...note, frequency: note.frequency * ratio };
}
function applyReferencePitch(tuning, a4) {
  if (a4 === STANDARD_A4) return tuning;
  const ratio = a4 / STANDARD_A4;
  return {
    ...tuning,
    notes: tuning.notes.map((note) => scaleNote(note, ratio))
  };
}

export { AUTOHARP_TUNINGS, BANJO_4_TUNINGS, BANJO_5_TUNINGS, BANJO_6_TUNINGS, BASS_4_TUNINGS, BASS_5_TUNINGS, BASS_6_TUNINGS, BOUZOUKI_TUNINGS, CAVAQUINHO_TUNINGS, CELLO_TUNINGS, CHARANGO_TUNINGS, CIGAR_BOX_TUNINGS, DOUBLE_BASS_TUNINGS, DULCIMER_TUNINGS, FEATURED_TUNING_IDS, GUITAR_7_STRING_TUNINGS, GUITAR_8_STRING_TUNINGS, GUITAR_TUNINGS, INSTRUMENT_CATEGORIES, LAP_STEEL_TUNINGS, MANDOCELLO_TUNINGS, MANDOLA_TUNINGS, MANDOLIN_TUNINGS, MAX_REFERENCE_PITCH, MICROTONAL_CATEGORY, MICROTONAL_GUITAR_TUNINGS, MIN_REFERENCE_PITCH, OUD_TUNINGS, PEDAL_STEEL_TUNINGS, PIPA_TUNINGS, REFERENCE_PITCH_PRESETS, SHAMISEN_TUNINGS, SITAR_TUNINGS, STANDARD_A4, UKULELE_TUNINGS, VIOLA_TUNINGS, VIOLIN_TUNINGS, applyReferencePitch, clampReferencePitch, createCustomTuning, createMicrotonalNote, createMicrotonalTuning, findTuningById, findTuningByNotes, frequencyToNote, getAllTunings, getDefaultTuning, getFeaturedTunings, getInstrumentById, getInstrumentForTuning, getSectionById, getSectionForTuning, getSectionsForInstrument, midiToFrequency, parseTuningString, searchTunings, serializeTuning };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map